import axios from "axios";
import moment from "moment";
import React, { useEffect, useState, useMemo } from "react";
import { Skeleton } from "@chakra-ui/react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Plus from "../../assets/svgs/Plus.svg";
import addPropertyIcon from "../../assets/svgs/add-property.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import ImportExport from "../../components/ImportExport";
import Loader from "../../components/Loader";
import DataAsOfNotice from "../../components/DataAsOfNotice";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import { ROUTES } from "../../routes/routeKeys";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import addOwnerIcon from "../../assets/svgs/add-owner.svg";
import { initialPropertyFilterData } from "../../utils/initialData";
import "./Properties.css";

import ColumnCustomizer from "../../components/ColumnCustomizer";
import PropertyAdvanceFilter from "./components/PropertyAdvanceFilter";
import { FiSettings } from "react-icons/fi";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const Properties = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const [tagsss, setTagsss] = useState([]);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [loadingOwner, setLoadingOwner] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [currentPageOwner, setCurrentPageOwner] = useState(1);
  const [itemPerPageOwner, setItemPerPageOwner] = useState(10);
  const [selectedItem, setSelectedItem] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortByOwner, setSortByOwner] = useState("updated_at");
  const [sortDirectionOwner, setSortDirectionOwner] = useState("desc");
  const [propertylistingdata, setPropertylistingdata] = useState([]);
  const [ownerlistingData, setOwnerlistingData] = useState([]);
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });
  const [paginationOwnerData, setPaginationOwnerData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialPropertyFilterData;
  });
  const [advanceFilterData, setAdvanceFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : [];
  });

  // ==================== COLUMN CUSTOMIZATION ====================
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showOwnerColumnModal, setShowOwnerColumnModal] = useState(false);

  // Columns for Properties Table
  const propertyColumns = [
    { key: 'select', label: 'Select', fixed: true },
    { key: 'property_name', label: 'Property Name', fixed: true },
    { key: 'property_type', label: 'Property Type' },
    { key: 'property_address', label: 'Property Address' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'owner', label: 'Owner' },
    { key: 'last_contacted', label: 'Last Contacted' },
    { key: 'last_update', label: 'Last Update' },
    { key: 'tags', label: 'Tags' },
  ];

  // Columns for Owners Table
  const ownerColumns = [
    { key: 'name', label: 'Name', fixed: true },
    { key: 'type', label: 'Type' },
    { key: 'phone', label: 'Phone' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
  ];

  const [visiblePropertyColumns, setVisiblePropertyColumns] = useState(() => {
    const saved = localStorage.getItem('propertyVisibleColumns');
    return saved ? JSON.parse(saved) : propertyColumns.map(col => col.key);
  });

  const [visibleOwnerColumns, setVisibleOwnerColumns] = useState(() => {
    const saved = localStorage.getItem('ownerVisibleColumns');
    return saved ? JSON.parse(saved) : ownerColumns.map(col => col.key);
  });
  // ============================================================

  const queryParams = new URLSearchParams(location.search);

  // Search states
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  const propertiesAllIds = propertylistingdata.map((el) => el.id);

  const handleSelectCheck = (item) => {
    const isSelected = selectedItem.some((el) => el.id === item.id);
    if (isSelected) {
      setSelectedItem(selectedItem.filter((el) => el.id !== item.id));
    } else {
      setSelectedItem([...selectedItem, item]);
    }
  };

  useEffect(() => {
    if (selectedItem.length === propertiesAllIds.length) {
      setIsSelectAll(true);
    } else {
      setIsSelectAll(false);
    }
  }, [selectedItem, propertiesAllIds]);

  // ====================== buildQueryParams & fetch functions (unchanged) ======================
  const buildQueryParams = (filters) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`;
    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }

    const { propertyName, year_built_date, storeId, propertyType, max_sold_price, without_property_tags, interaction_type_user, min_sold_price, lastcontactCategory, longer_than_contact_day, within_last_contact_day, min_sold_cap_rate, max_sold_cap_rate, streetAddress, interaction_type, owner_id, city, state, zip, owner, ownerType, count, start_count, end_count, lastUpdateOption, sentDate, lastUpStartDate, lastUpEndDate, lastUpDateCategory, withinLastDay, longerThanDay, minCaprate, maxCaprate, minPrice, maxPrice, minBuilding,
      maxBuilding, minLand, maxLand, minVacancy, maxVacancy,
      lastSoldOption, lastSoldSentDate, lastSoldStartDate,
      lastSoldEndDate, lastSoldCategory, lastSoldWithinLastValue,
      lastSoldLongerThanValue, owner_without_tags, owner_tags, hasOwner,
      ownerPhoneCheck, ownerPhone, ownerStreetAddressCheck, ownerStreetAddress,
      ownerTaxRecordCheck, taxRecordSentDate, taxRecordSentStartDate, taxRecordSentEndDate,
      ownerWebsite, ownerTags, tags, last_contact, lastContactSentDate, lastContactSentEdate,
      lastContactSentSdate, year_count, start_year_build_count, end_year_build_count } = filters;

    if (propertyName) params += `&property_name=${encodeURIComponent(propertyName)}`;
    if (storeId) params += `&store_id=${encodeURIComponent(storeId)}`;
    if (year_count) params += `&year_count=${encodeURIComponent(year_count)}`;
    if (start_year_build_count) params += `&start_year_build_count=${encodeURIComponent(start_year_build_count)}`;
    if (end_year_build_count) params += `&end_year_build_count=${encodeURIComponent(end_year_build_count)}`;
    if (propertyType) params += `&property_type=${encodeURIComponent(propertyType)}`;
    if (streetAddress) params += `&street_address=${encodeURIComponent(streetAddress)}`;
    if (city) params += `&city=${encodeURIComponent(city)}`;
    if (state) params += `&state=${encodeURIComponent(state)}`;
    if (zip) params += `&zip_code=${encodeURIComponent(zip)}`;
    if (lastUpdateOption) params += `&last_update=${encodeURIComponent(lastUpdateOption)}`;
    if (sentDate) params += `&last_update_date=${encodeURIComponent(sentDate)}`;
    if (lastUpStartDate) params += `&last_update_start_date=${encodeURIComponent(lastUpStartDate)}`;
    if (lastUpEndDate) params += `&last_update_end_date=${encodeURIComponent(lastUpEndDate)}`;
    if (lastUpDateCategory) params += `&date_category=${encodeURIComponent(lastUpDateCategory)}`;
    if (lastcontactCategory) params += `&date_category_contact=${encodeURIComponent(lastcontactCategory)}`;
    if (interaction_type_user) params += `&interaction_type_user=${encodeURIComponent(interaction_type_user)}`;
    if (withinLastDay) params += `&within_last_day=${encodeURIComponent(withinLastDay)}`;
    if (within_last_contact_day) params += `&within_last_contact_day=${encodeURIComponent(within_last_contact_day)}`;
    if (longerThanDay) params += `&longer_than_day=${encodeURIComponent(longerThanDay)}`;
    if (longer_than_contact_day) params += `&longer_than_contact_day=${encodeURIComponent(longer_than_contact_day)}`;
    if (minCaprate) params += `&min_rate=${encodeURIComponent(minCaprate)}`;
    if (maxCaprate) params += `&max_rate=${encodeURIComponent(maxCaprate)}`;
    if (minBuilding) params += `&building_min_size=${encodeURIComponent(minBuilding)}`;
    if (maxBuilding) params += `&building_max_size=${encodeURIComponent(maxBuilding)}`;
    if (minPrice) params += `&min_price=${encodeURIComponent(minPrice)}`;
    if (maxPrice) params += `&max_price=${encodeURIComponent(maxPrice)}`;
    if (minLand) params += `&land_min_size=${encodeURIComponent(minLand)}`;
    if (maxLand) params += `&land_max_size=${encodeURIComponent(maxLand)}`;
    if (ownerType) params += `&owner_type=${encodeURIComponent(ownerType)}`;
    if (owner?.id) params += `&owner=${encodeURIComponent(owner?.id)}`;
    if (hasOwner) params += `&has_owner=${encodeURIComponent(hasOwner)}`;
    if (ownerPhoneCheck) params += `&has_owner_phone=${encodeURIComponent(ownerPhoneCheck)}`;
    if (ownerPhone) params += `&owner_phone=${encodeURIComponent(ownerPhone)}`;
    if (ownerStreetAddressCheck) params += `&has_owner_street_adrees=${encodeURIComponent(ownerStreetAddressCheck)}`;
    if (ownerStreetAddress) params += `&owner_street_adrees=${encodeURIComponent(ownerStreetAddress)}`;
    if (ownerWebsite) params += `&owner_website=${encodeURIComponent(ownerWebsite)}`;
    if (ownerTaxRecordCheck) params += `&has_owner_tax_record=${encodeURIComponent(ownerTaxRecordCheck)}`;
    if (taxRecordSentDate) params += `&tax_record_sent_date=${encodeURIComponent(taxRecordSentDate)}`;
    if (taxRecordSentStartDate) params += `&tax_record_start_date=${encodeURIComponent(taxRecordSentStartDate)}`;
    if (taxRecordSentEndDate) params += `&tax_record_end_date=${encodeURIComponent(taxRecordSentEndDate)}`;
    if (tags) params += `&tags=${encodeURIComponent(tags)}`;
    if (without_property_tags) params += `&without_property_tags=${encodeURIComponent(without_property_tags)}`;
    if (owner_tags?.length > 0) params += `&owner_tags=${encodeURIComponent(owner_tags)}`;
    if (owner_without_tags) params += `&owner_without_tags=${encodeURIComponent(owner_without_tags?.join(","))}`;
    if (lastSoldOption) params += `&last_sold=${encodeURIComponent(lastSoldOption)}`;
    if (lastSoldSentDate) params += `&last_sold_date=${encodeURIComponent(lastSoldSentDate)}`;
    if (lastSoldStartDate) params += `&last_sold_start_date=${encodeURIComponent(lastSoldStartDate)}`;
    if (lastSoldEndDate) params += `&last_sold_end_date=${encodeURIComponent(lastSoldEndDate)}`;
    if (lastSoldCategory) params += `&sold_date_category=${encodeURIComponent(lastSoldCategory)}`;
    if (lastSoldWithinLastValue) params += `&sold_within_last_day=${encodeURIComponent(lastSoldWithinLastValue)}`;
    if (lastSoldLongerThanValue) params += `&sold_longer_than_day=${encodeURIComponent(lastSoldLongerThanValue)}`;
    if (last_contact) params += `&last_contact=${encodeURIComponent(last_contact)}`;
    if (lastContactSentDate !== "") params += `&last_update_contact_date=${encodeURIComponent(lastContactSentDate)}`;
    if (lastContactSentSdate !== "") params += `&last_update_start_contact_date=${encodeURIComponent(lastContactSentSdate)}`;
    if (lastContactSentEdate !== "") params += `&last_update_end_contact_date=${encodeURIComponent(lastContactSentEdate)}`;
    if (count) params += `&count=${encodeURIComponent(count)}`;
    if (start_count) params += `&start_count=${encodeURIComponent(start_count)}`;
    if (end_count) params += `&end_count=${encodeURIComponent(end_count)}`;
    if (owner_id) params += `&owner_id=${encodeURIComponent(owner_id)}`;
    if (min_sold_price) params += `&min_sold_price=${encodeURIComponent(min_sold_price)}`;
    if (max_sold_price) params += `&max_sold_price=${encodeURIComponent(max_sold_price)}`;
    if (min_sold_cap_rate) params += `&min_sold_cap_rate=${encodeURIComponent(min_sold_cap_rate)}`;
    if (max_sold_cap_rate) params += `&max_sold_cap_rate=${encodeURIComponent(max_sold_cap_rate)}`;
    if (sortDirection && sortBy !== "id") params += `&sort_direction=${sortDirection}`;
    if (sortBy !== "id") params += `&sort_by=${sortBy}`;
    params += `&type=property`;
    localStorage.setItem("queryParams", params);
    return params;
  };

   const buildQueryParamsOwner = (filters) => {
     let params = `page=${currentPageOwner}&per_page=${itemPerPageOwner}`;
    console.log('buildQueryParamsOwner params:', params);
    console.log('Filters in buildQueryParamsOwner:', filters);
    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }

    const { propertyName, storeId, propertyType, streetAddress, city, state, zip, owner, ownerType, count,
      start_count, end_count, lastUpdateOption, sentDate, lastUpStartDate, lastUpEndDate, lastUpDateCategory,
      withinLastDay, longerThanDay, minCaprate, maxCaprate, minPrice, maxPrice, minBuilding, maxBuilding,
      minLand, maxLand, minVacancy, maxVacancy, lastSoldOption, lastSoldSentDate, lastSoldStartDate,
      lastSoldEndDate, lastSoldCategory, lastSoldWithinLastValue, lastSoldLongerThanValue,
      owner_without_tags, owner_tags, hasOwner, ownerPhoneCheck, ownerPhone, ownerStreetAddressCheck
      , ownerStreetAddress, ownerTaxRecordCheck, taxRecordSentDate, taxRecordSentStartDate,
      taxRecordSentEndDate, ownerWebsite, ownerTags, tags, year_count, start_year_build_count, end_year_build_count } = filters;

    if (propertyName !== undefined) {
      params += `&property_name=${encodeURIComponent(propertyName)}`;
    }
    if (storeId !== undefined) {
      params += `&store_id=${encodeURIComponent(storeId)}`;
    }
    if (propertyType !== undefined) {
      params += `&property_type=${encodeURIComponent(propertyType)}`;
    }
    if (streetAddress !== undefined) {
      params += `&street_address=${encodeURIComponent(streetAddress)}`;
    }
    if (city !== undefined) {
      params += `&city=${encodeURIComponent(city)}`;
    }
    if (state !== undefined) {
      params += `&state=${encodeURIComponent(state)}`;
    }
    if (zip !== undefined) {
      params += `&zip_code=${encodeURIComponent(zip)}`;
    }
    if (year_count) {
      params += `&year_count=${encodeURIComponent(year_count)}`;
    }
    if (start_year_build_count) {
      params += `&start_year_build_count=${encodeURIComponent(start_year_build_count)}`;
    }
    if (end_year_build_count) {
      params += `&end_year_build_count=${encodeURIComponent(end_year_build_count)}`;
    }
    if ((lastUpdateOption !== undefined && sentDate !== undefined) || lastUpStartDate !== undefined || lastUpEndDate !== undefined) {
      params += `&last_update=${encodeURIComponent(lastUpdateOption)}`;
    }
    if (sentDate !== undefined) {
      params += `&last_update_date=${encodeURIComponent(sentDate)}`;
    }
    if (lastUpStartDate !== undefined) {
      params += `&last_update_start_date=${encodeURIComponent(lastUpStartDate)}`;
    }
    if (lastUpEndDate !== undefined) {
      params += `&last_update_end_date=${encodeURIComponent(lastUpEndDate)}`;
    }
    if (lastUpDateCategory !== undefined) {
      params += `&date_category=${encodeURIComponent(lastUpDateCategory)}`;
    }
    if (withinLastDay !== undefined) {
      params += `&within_last_day=${encodeURIComponent(withinLastDay)}`;
    }
    if (longerThanDay !== undefined) {
      params += `&longer_than_day=${encodeURIComponent(longerThanDay)}`;
    }
    if (minCaprate !== undefined) {
      params += `&min_rate=${encodeURIComponent(minCaprate)}`;
    }
    if (maxCaprate !== undefined) {
      params += `&max_rate=${encodeURIComponent(maxCaprate)}`;
    }
    if (minBuilding !== undefined) {
      params += `&building_min_size=${encodeURIComponent(minBuilding)}`;
    }
    if (maxBuilding !== undefined) {
      params += `&building_max_size=${encodeURIComponent(maxBuilding)}`;
    }
    if (minPrice !== undefined) {
      params += `&min_price=${encodeURIComponent(minPrice)}`;
    }
    if (maxPrice !== undefined) {
      params += `&max_price=${encodeURIComponent(maxPrice)}`;
    }
    if (minLand !== undefined) {
      params += `&land_min_size=${encodeURIComponent(minLand)}`;
    }
    if (maxLand !== undefined) {
      params += `&land_max_size=${encodeURIComponent(maxLand)}`;
    }
    if (ownerType !== undefined) {
      params += `&owner_type=${encodeURIComponent(ownerType)}`;
    }
    if (owner?.id !== undefined) {
      params += `&owner=${encodeURIComponent(owner.id)}`;
    }
    if (hasOwner !== undefined) {
      params += `&has_owner=${encodeURIComponent(hasOwner)}`;
    }
    if (ownerPhoneCheck !== undefined) {
      params += `&has_owner_phone=${encodeURIComponent(ownerPhoneCheck)}`;
    }
    if (ownerPhone !== undefined) {
      params += `&owner_phone=${encodeURIComponent(ownerPhone)}`;
    }
    if (ownerStreetAddressCheck !== undefined) {
      params += `&has_owner_street_adrees=${encodeURIComponent(ownerStreetAddressCheck)}`;
    }
    if (ownerStreetAddress !== undefined) {
      params += `&owner_street_adrees=${encodeURIComponent(ownerStreetAddress)}`;
    }
    if (ownerWebsite !== undefined) {
      params += `&owner_website=${encodeURIComponent(ownerWebsite)}`;
    }
    if (ownerTaxRecordCheck !== undefined) {
      params += `&has_owner_tax_record=${encodeURIComponent(ownerTaxRecordCheck)}`;
    }
    if (taxRecordSentDate !== undefined) {
      params += `&tax_record_sent_date=${encodeURIComponent(taxRecordSentDate)}`;
    }
    if (taxRecordSentStartDate !== undefined) {
      params += `&tax_record_start_date=${encodeURIComponent(taxRecordSentStartDate)}`;
    }
    if (taxRecordSentEndDate !== undefined) {
      params += `&tax_record_end_date=${encodeURIComponent(taxRecordSentEndDate)}`;
    }
    if (tags !== undefined) {
      params += `&tags=${encodeURIComponent(tags)}`;
    }
    if (owner_tags?.length > 0) {
      params += `&owner_tags=${encodeURIComponent(owner_tags)}`;
    }
    if (owner_without_tags !== undefined) {
      params += `&owner_without_tags=${encodeURIComponent(owner_without_tags?.join(","))}`;
    }
    if ((lastSoldOption !== undefined && lastSoldSentDate !== undefined) || lastSoldStartDate !== undefined || lastSoldEndDate !== undefined) {
      params += `&last_sold=${encodeURIComponent(lastSoldOption)}`;
    }
    if (lastSoldSentDate !== undefined) {
      params += `&last_sold_date=${encodeURIComponent(lastSoldSentDate)}`;
    }
    if (lastSoldStartDate !== undefined) {
      params += `&last_sold_start_date=${encodeURIComponent(lastSoldStartDate)}`;
    }
    if (lastSoldEndDate !== undefined) {
      params += `&last_sold_end_date=${encodeURIComponent(lastSoldEndDate)}`;
    }
    if (lastSoldCategory !== undefined) {
      params += `&sold_date_category=${encodeURIComponent(lastSoldCategory)}`;
    }
    if (lastSoldWithinLastValue !== undefined) {
      params += `&sold_within_last_day=${encodeURIComponent(lastSoldWithinLastValue)}`;
    }
    if (lastSoldLongerThanValue !== undefined) {
      params += `&sold_longer_than_day=${encodeURIComponent(lastSoldLongerThanValue)}`;
    }
    if (count !== undefined) {
      params += `&count=${encodeURIComponent(count)}`;
    }
    if (start_count !== undefined) {
      params += `&start_count=${encodeURIComponent(start_count)}`;
    }
    if (end_count !== undefined) {
      params += `&end_count=${encodeURIComponent(end_count)}`;
    }
    if (selectedItem?.length > 0) {
      params += `&property_id[]=${selectedItem.join("&property_id[]=")}`;
    }
    if (sortDirection !== undefined && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortDirectionOwner !== undefined && sortByOwner !== "id") {
      params += `&sort_direction_owner=${sortDirectionOwner}`;
    }
    if (sortBy !== "id") {
      params += `&sort_by=${sortBy}`;
    }
    if (sortByOwner !== "id") {
      params += `&sort_by_owner=${sortByOwner}`;
    }


    params += `&type=property`;
    localStorage.setItem("queryParams", params);
    return params;

  };

  const fetchProperties = (filters, advanceFilters = []) => {
    const queryParams = buildQueryParams(filters);
    const validAdvanceFilters = advanceFilters.filter(group =>
      group.filters && group.filters.some(f => f.field && f.operator)
    );
    const advanceFiltersQuery = encodeURIComponent(JSON.stringify(validAdvanceFilters));

    axios
      .get(`${BASE_URL}/property-list?${queryParams}&advance_filters=${advanceFiltersQuery}`, config)
      .then((res) => {
        const value = res?.data?.properties;
        setPropertylistingdata(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
        setSelectedItem([]);
        setIsSelectAll(false);
      })
      .catch((error) => {
        if (error.response?.data?.message) NotificationManager.error(error.response?.data?.message);
      })
      .finally(() => setLoading(false));
  };

  const fetchOwners = (filters, advanceFilters = []) => {
    const queryParams = buildQueryParamsOwner(filters);
    const validAdvanceFilters = advanceFilters.filter(group =>
      group.filters && group.filters.some(f => f.field && f.operator)
    );
    const advanceFiltersQuery = encodeURIComponent(JSON.stringify(validAdvanceFilters));

    axios
      .get(`${BASE_URL}/property-owner-list?${queryParams}&advance_filters=${advanceFiltersQuery}`, config)
      .then((res) => {
        const value1 = res?.data?.data?.properties;
        setOwnerlistingData(value1?.data || []);
        setPaginationOwnerData({
          totalItems: value1?.total ?? 0,
          from: value1?.from ?? 0,
          to: value1?.to ?? 0,
          totalPages: value1?.last_page ?? 0,
        });
      })
      .catch((error) => {
        if (error.response?.data?.message) NotificationManager.error(error.response?.data?.message);
      })
      .finally(() => setLoadingOwner(false));
  };

  useEffect(() => {
    fetchProperties(filterData, advanceFilterData);
    fetchOwners(filterData, advanceFilterData);
  }, [sortDirection, sortBy, sortDirectionOwner, sortByOwner]);

  useEffect(() => {
    onSuccess();
  }, [currentPage, itemPerPage, search, advanceFilterData]);

  useEffect(() => {
    onOwnerSuccess();
  }, [currentPageOwner, itemPerPageOwner, search, advanceFilterData]);

  const onSuccess = (filters, advanceFilters) => {
    setLoading(true);
    const normalFilters = filters || filterData;
    const advFilters = advanceFilters !== undefined ? advanceFilters : advanceFilterData;
    fetchProperties(normalFilters, advFilters);
  };

  const onOwnerSuccess = (filters, advanceFilters) => {
    setLoadingOwner(true);
    const normalFilters = filters || filterData;
    const advFilters = advanceFilters !== undefined ? advanceFilters : advanceFilterData;
    fetchOwners(normalFilters, advFilters);
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextOwner = () => {
    if (currentPageOwner !== paginationOwnerData.totalPages) setCurrentPageOwner(currentPageOwner + 1);
  };

  const handlePrevOwner = () => {
    if (currentPageOwner > 1) setCurrentPageOwner(currentPageOwner - 1);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  // Debounce & URL sync effects (unchanged)
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  return (
    <BaseLayout>
      <div className="">
        <div className="contact-header">
          <p className="head-1 dark-H">
            Properties
            {selectedItem?.length > 0 && (
              <span className="body-L ml-2">
                ({selectedItem?.length} <span className="dark-H">{selectedItem?.length === 1 ? "item" : "items"} selected</span>)
              </span>
            )}
          </p>

        </div>

        <div className="md:flex justify-between items-center mb-4 mt-2">
          <div className="flex gap-4 items-center flex-wrap">
            <PropertyAdvanceFilter
              filterData={advanceFilterData}
              onSetFilterData={setAdvanceFilterData}
              onCallApiAgain={(newAdvanceFilters) => {
                setAdvanceFilterData(newAdvanceFilters);
                fetchProperties(filterData, newAdvanceFilters);
              }}
            />

            <ImportExport
              from="property"
              onCallApi={() => {
                onSuccess();
                setSelectedItem([]);
              }}
              selectedData={selectedItem.length !== 0 ? propertylistingdata?.filter((el) => selectedItem?.includes(el?.id)) : []}
              advanceFilterData={advanceFilterData}
            />

            <div className="search-box contacts relative w-[250px]">
              <input
                type="text"
                className="body-S"
                placeholder="Search Property, Owner, City, State... (min 3 characters)"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8", marginTop: "0px", width: "100%" }}
              />
            </div>
          </div>

          <div className="flex gap-2 items-center">
            <button
              className="add-contact-button green-bg-H light-L body-S flex items-center gap-1"
              onClick={() => setShowColumnModal(true)}
            >
               <FiSettings className="w-4 h-4" />Columns 
            </button>
            <button className="add-contact-button green-bg-H light-L body-S" onClick={() => navigate(ROUTES.ADD_PROPERTY)}>
              <img className="mr-2 sidebar-icons" src={Plus} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      {/* ==================== PROPERTIES TABLE ==================== */}
      <div className="light-bg-L pb-3 table-container bg-white rounded-xl border border-gray-200 overflow-hidden relative min-h-[400px]">
        {loading && (
          <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
            <Loader />
          </div>
        )}
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
          <table className="contact-table two-rows-static light-bg-L">
              <thead>
                <tr className="uppercase body-N dark-M">
                  {visiblePropertyColumns.includes('select') && (
                    <th>
                      <label className="container">
                        <input type="checkbox" checked={selectedItem.length > 0 && selectedItem.length === propertylistingdata.length}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setIsSelectAll(isChecked);
                            if (isChecked) setSelectedItem([...propertylistingdata]);
                            else setSelectedItem([]);
                          }}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </th>
                  )}

                  {visiblePropertyColumns.includes('property_name') && (
                    <th className="green-H">
                      <div className="table-header">
                        Property Name
                        <img role="button" src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("property_name"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visiblePropertyColumns.includes('property_type') && (
                    <th>
                      <div className="table-header">Property Type
                        <img role="button" src={sortBy !== "property_type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("property_type"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visiblePropertyColumns.includes('property_address') && (
                    <th>
                      <div className="table-header">Property Address
                        <img role="button" src={sortBy !== "street_address" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("street_address"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visiblePropertyColumns.includes('city') && (
                    <th>
                      <div className="table-header">City
                        <img role="button" src={sortBy !== "city" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("city"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visiblePropertyColumns.includes('state') && (
                    <th>
                      <div className="table-header">State
                        <img role="button" src={sortBy !== "state" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("state"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visiblePropertyColumns.includes('owner') && (
                    <th>
                      <div className="table-header">Owner
                        <img role="button" src={sortBy !== "owner" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("owner"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visiblePropertyColumns.includes('last_contacted') && (
                    <th>
                      <div className="table-header">Last Contacted
                        <img role="button" src={sortBy !== "last_connected_date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("last_connected_date"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visiblePropertyColumns.includes('last_update') && (
                    <th>
                      <div className="table-header">Last Update
                        <img role="button" src={sortBy !== "updated_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("updated_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visiblePropertyColumns.includes('tags') && (
                    <th>
                      <div className="table-header">Tags
                        <img role="button" src={sortBy !== "propertytag_id" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("propertytag_id"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  [...Array(10)].map((_, idx) => (
                    <tr key={idx} className="dark-H body-N">
                      {visiblePropertyColumns.includes('select') && <td><Skeleton height="20px" width="20px" /></td>}
                      {visiblePropertyColumns.includes('property_name') && <td><Skeleton height="20px" width="140px" /></td>}
                      {visiblePropertyColumns.includes('property_type') && <td><Skeleton height="20px" width="100px" /></td>}
                      {visiblePropertyColumns.includes('property_address') && <td><Skeleton height="20px" width="120px" /></td>}
                      {visiblePropertyColumns.includes('city') && <td><Skeleton height="20px" width="100px" /></td>}
                      {visiblePropertyColumns.includes('state') && <td><Skeleton height="20px" width="60px" /></td>}
                      {visiblePropertyColumns.includes('owner') && <td><Skeleton height="20px" width="120px" /></td>}
                      {visiblePropertyColumns.includes('last_contacted') && <td><Skeleton height="20px" width="100px" /></td>}
                      {visiblePropertyColumns.includes('last_update') && <td><Skeleton height="20px" width="100px" /></td>}
                      {visiblePropertyColumns.includes('tags') && <td><Skeleton height="20px" width="80px" /></td>}
                    </tr>
                  ))
                ) : propertylistingdata.length === 0 ? (
                  <tr style={{ border: "none" }} className="border-none">
                    <td colSpan={visiblePropertyColumns.length} className="body-N text-center mt-6 p-10 border-none" style={{ border: "none" }}>
                      <div className="flex flex-col items-center justify-center py-12">
                        <img src={addPropertyIcon} alt="No Properties" className="w-16 h-16 mb-4 opacity-60" />
                        <p className="head-4 dark-H mb-2">No Properties Found</p>
                        <p className="body-N dark-M mb-6">There are no properties matching your search or filters.</p>
                        <button
                          className="add-contact-button green-bg-H light-L body-S flex items-center gap-2"
                          onClick={() => navigate(ROUTES.ADD_PROPERTY)}
                        >
                          <img className="sidebar-icons" src={Plus} alt="plus" /> Add Property
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  propertylistingdata.map((el, idx) => (
                    <tr key={idx} className="dark-H body-N">
                    {visiblePropertyColumns.includes('select') && (
                      <td>
                        <label className="container">
                          <input type="checkbox" checked={selectedItem.some((item) => item.id === el.id)}
                            onChange={() => handleSelectCheck(el)} />
                          <span className="checkmark"></span>
                        </label>
                      </td>
                    )}

                    {visiblePropertyColumns.includes('property_name') && (
                      <td className="head-5 normal-case cursor-pointer">
                        <Link to={`${ROUTES.PROPERTY_DETAILS.replace(":id", el.id)}?search=${search}`} onClick={(e) => { e.preventDefault(); window.open(`${ROUTES.PROPERTY_DETAILS.replace(":id", el.id)}?search=${search}`, "_blank"); }}>
                          {el.property_name}
                        </Link>
                      </td>
                    )}

                    {visiblePropertyColumns.includes('property_type') && <td className="dark-M capitalize">{el.property_type?.type}</td>}
                    {visiblePropertyColumns.includes('property_address') && <td>{el.street_address}</td>}
                    {visiblePropertyColumns.includes('city') && <td className="capitalize">{el.city}</td>}
                    {visiblePropertyColumns.includes('state') && <td>{el.state}</td>}

                    {visiblePropertyColumns.includes('owner') && (
                      <td className="head-5 green-H capitalize">
                        <Link to={el?.owner_company_id ? ROUTES.FILTER_DATA.replace(":id", el.owner_company_id).replace(":propertyId", "company") : ROUTES.FILTER_DATA.replace(":id", el.owner_contact_id).replace(":propertyId", "contact")}
                          onClick={(e) => {
                            e.preventDefault();
                            if (el?.owner_company_id) window.open(ROUTES.FILTER_DATA.replace(":id", el.owner_company_id).replace(":propertyId", "company"), "_blank");
                            else window.open(ROUTES.FILTER_DATA.replace(":id", el.owner_contact_id).replace(":propertyId", "contact"), "_blank");
                          }}>
                          {el.owner?.name}
                        </Link>
                      </td>
                    )}

                    {visiblePropertyColumns.includes('last_contacted') && (
                      <td className="dark-M">
                        {/* Your existing last contacted logic */}
                        {el.owner_contact_id && el.contact?.latest_activity_including_contacts?.date_connected
                          ? moment(el.contact.latest_activity_including_contacts.date_connected).tz('America/New_York').format("MM/DD/YYYY")
                          : el.owner_company_id && el.company?.latest_activity_including_contacts?.date_connected
                            ? moment(el.company.latest_activity_including_contacts.date_connected).tz('America/New_York').format("MM/DD/YYYY") : ''}
                      </td>
                    )}

                    {visiblePropertyColumns.includes('last_update') && (
                      <td className="dark-M">{moment(el.updated_at).tz('America/New_York').format("MM/DD/YYYY")}</td>
                    )}

                    {visiblePropertyColumns.includes('tags') && (
                      <td className="flex flex-wrap items-center gap-2" style={{ maxWidth: "450px" }}>
                        {el?.propertytag_details?.map((tag, i) => (
                          <p key={i} className="tags green-H body-S">{tag?.tag_name}</p>
                        ))}
                      </td>
                    )}
                  </tr>
                ))
                )}
              </tbody>
            </table>
        </div>

        {propertylistingdata?.length !== 0 && (
          <ContactPagination
            from="property"
            selectedItem={selectedItem}
            paginationData={paginationData}
            handleNext={handleNext}
            tags={tagsss}
            setTags={setTagsss}
            handlePrev={handlePrev}
            onSuccess={onSuccess}
            itemPerPage={itemPerPage}
            handleItemPerPageSelect={(value) => { setItemPerPage(value); setCurrentPage(1); }}
          />
        )}
        <div className="px-6 pb-2">
          <DataAsOfNotice  />
        </div>
      </div>

      {/* ==================== OWNERS SECTION ==================== */}
      <div className="contact-header">
        <p className="head-1 dark-H pt-3">Owners</p>
        <div className="flex gap-2">
          <button
            className="add-contact-button green-bg-H light-L body-S flex items-center gap-1"
            onClick={() => setShowOwnerColumnModal(true)}
          >
            <FiSettings className="w-4 h-4" /> Columns 
          </button>
        </div>
      </div>

      <div className="light-bg-L pb-3 table-container bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
          <table className="contact-table two-rows-static light-bg-L">
              <thead>
                <tr className="uppercase body-N dark-M">
                  {visibleOwnerColumns.includes('name') && (
                    <th className="green-H">
                      <div className="table-header">
                        Name
                        <img role="button" src={sortByOwner !== "owner" ? swap : sortDirectionOwner === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortByOwner("owner"); setSortDirectionOwner(sortDirectionOwner === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleOwnerColumns.includes('type') && (
                    <th>
                      <div className="table-header">Type
                        <img role="button" src={sortByOwner !== "owner_info_type" ? swap : sortDirectionOwner === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortByOwner("owner_info_type"); setSortDirectionOwner(sortDirectionOwner === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleOwnerColumns.includes('phone') && (
                    <th>
                      <div className="table-header">Phone
                        <img role="button" src={sortByOwner !== "owner_phonenumber" ? swap : sortDirectionOwner === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortByOwner("owner_phonenumber"); setSortDirectionOwner(sortDirectionOwner === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleOwnerColumns.includes('address') && (
                    <th>
                      <div className="table-header">Address
                        <img role="button" src={sortByOwner !== "owner_address" ? swap : sortDirectionOwner === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortByOwner("owner_address"); setSortDirectionOwner(sortDirectionOwner === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleOwnerColumns.includes('city') && (
                    <th>
                      <div className="table-header">City
                        <img role="button" src={sortByOwner !== "owner_city" ? swap : sortDirectionOwner === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortByOwner("owner_city"); setSortDirectionOwner(sortDirectionOwner === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleOwnerColumns.includes('state') && (
                    <th>
                      <div className="table-header">State
                        <img role="button" src={sortByOwner !== "owner_state" ? swap : sortDirectionOwner === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortByOwner("owner_state"); setSortDirectionOwner(sortDirectionOwner === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {loadingOwner ? (
                  [...Array(10)].map((_, idx) => (
                    <tr key={idx} className="dark-H body-N">
                      {visibleOwnerColumns.includes('name') && <td><Skeleton height="20px" width="140px" /></td>}
                      {visibleOwnerColumns.includes('type') && <td><Skeleton height="20px" width="100px" /></td>}
                      {visibleOwnerColumns.includes('phone') && <td><Skeleton height="20px" width="120px" /></td>}
                      {visibleOwnerColumns.includes('address') && <td><Skeleton height="20px" width="150px" /></td>}
                      {visibleOwnerColumns.includes('city') && <td><Skeleton height="20px" width="100px" /></td>}
                      {visibleOwnerColumns.includes('state') && <td><Skeleton height="20px" width="60px" /></td>}
                    </tr>
                  ))
                ) : ownerlistingData.length === 0 ? (
                  <tr style={{ border: "none" }} className="border-none">
                    <td colSpan={visibleOwnerColumns.length} className="body-N text-center mt-6 p-10 border-none" style={{ border: "none" }}>
                      <div className="flex flex-col items-center justify-center py-12">
                        <img src={addOwnerIcon} alt="No Owners" className="w-16 h-16 mb-4 opacity-60" />
                        <p className="head-4 dark-H mb-2">No Owners Found</p>
                        <p className="body-N dark-M mb-6">There are no owners matching your search or filters.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  ownerlistingData.map((el, idx) => (
                    <tr key={idx} className="dark-H body-N">
                    {visibleOwnerColumns.includes('name') && (
                      <td className="head-5 normal-case cursor-pointer">
                        <Link to={el?.owner_company_id ? ROUTES.FILTER_DATA.replace(":id", el.owner_company_id).replace(":propertyId", "company") : ROUTES.FILTER_DATA.replace(":id", el.owner_contact_id).replace(":propertyId", "contact")}
                          onClick={(e) => {
                            e.preventDefault();
                            if (el?.owner_company_id) window.open(ROUTES.FILTER_DATA.replace(":id", el.owner_company_id).replace(":propertyId", "company"), "_blank");
                            else window.open(ROUTES.FILTER_DATA.replace(":id", el.owner_contact_id).replace(":propertyId", "contact"), "_blank");
                          }}>
                          {el.owner_name}
                        </Link>
                      </td>
                    )}

                    {visibleOwnerColumns.includes('type') && <td className="dark-M capitalize">{el.owner_info_type}</td>}
                    {visibleOwnerColumns.includes('phone') && <td>{el.owner_phone?.[0]?.phone_number}</td>}
                    {visibleOwnerColumns.includes('address') && <td>{el.owner_address?.[0]?.street}</td>}
                    {visibleOwnerColumns.includes('city') && <td>{el.owner_address?.[0]?.city}</td>}
                    {visibleOwnerColumns.includes('state') && <td>{el.owner_address?.[0]?.state}</td>}
                  </tr>
                ))
                )}
              </tbody>
            </table>
        </div>

        {ownerlistingData?.length !== 0 && (
          <ContactPagination
            from="property"
            selectedItem={selectedItem}
            paginationData={paginationOwnerData}
            handleNext={handleNextOwner}
            handlePrev={handlePrevOwner}
            onSuccess={onOwnerSuccess}
            itemPerPage={itemPerPageOwner}
            handleItemPerPageSelect={(value) => { setItemPerPageOwner(value); setCurrentPageOwner(1); }}
          />
        )}
        <div className="px-6 pb-2">
          <DataAsOfNotice  />
        </div>
      </div>

      {/* Column Customizer Modals */}
      {showColumnModal && (
        <ColumnCustomizer
          allColumns={propertyColumns}
          visibleColumns={visiblePropertyColumns}
          setVisibleColumns={setVisiblePropertyColumns}
          onClose={() => setShowColumnModal(false)}
          storageKey="propertyVisibleColumns"
        />
      )}

      {showOwnerColumnModal && (
        <ColumnCustomizer
          allColumns={ownerColumns}
          visibleColumns={visibleOwnerColumns}
          setVisibleColumns={setVisibleOwnerColumns}
          onClose={() => setShowOwnerColumnModal(false)}
          storageKey="ownerVisibleColumns"
        />
      )}
    </BaseLayout>
  );
};

export default Properties;